import React, { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

import { performUpdateInboundMini } from "../../../graphql/inbound/inboundCRUD";
import { performUpdateOutboundMini } from "../../../graphql/outbound/outboundCRUD";
import {
  performUpdateInvoiceMini,
  performUpdateInvoiceServiceMini,
} from "../../../graphql/invoice/invoiceCRUD";
import { API, graphqlOperation } from "aws-amplify";
import {
  inboundShipmentByReloadYardByInvoice,
  outboundShipmentByReloadYardByInvoice,
} from "../../../graphql/invoice/invoice-queries";

import { makeStyles, Tooltip } from "@material-ui/core";
import { alpha } from "@material-ui/core/styles/colorManipulator";
import classnames from "classnames";

import { useNotify, Button, Confirm } from "react-admin";

import { DeleteIcon, DeleteSyncIcon } from "../../../components/common/iconography";
import { logError, logHistory } from "../../../utils/appUtils";
import { deepRedirect, trimBackPath } from "../../../utils/utils";
import {
  hasExternalId,
  sendEmail,
  triggerEvent,
} from "../../../utils/zapierUtils";
import {
  ExternalStatus,
  InvoiceStatus,
  ResourceType,
} from "../../../utils/constants";
import { setInvoiceSyncFailed } from "./InvoiceUtils";
import { EmailInvoiceDialog } from "./SendInvoiceButtons";

const useStyles = makeStyles(
  (theme) => ({
    deleteButton: {
      color: theme.palette.error.main,
      "&:hover": {
        backgroundColor: alpha(theme.palette.error.main, 0.12),
        // Reset on mouse devices
        "@media (hover: none)": {
          backgroundColor: "transparent",
        },
      },
    },
  }),
  { name: "RaDeleteWithConfirmButton" },
);

const fetchShipmentsByInvoiceId = async ({
  query,
  queryName,
  invoiceId,
  reloadYardId,
}) => {
  const shipments = [];
  let nextToken = null;

  do {
    const response = await API.graphql(
      graphqlOperation(query, {
        reloadYardId,
        invoiceId: { eq: invoiceId },
        limit: 200,
        nextToken,
      }),
    );

    const page = response?.data?.[queryName];
    shipments.push(...(page?.items || []).filter(Boolean));
    nextToken = page?.nextToken;
  } while (nextToken);

  return shipments;
};

export const DeepDeleteButton = ({
  backPath,
  record,
  resource,
  className,
  isSaving,
  isDeleting,
  setIsDeleting,
  isSyncEnabled,
  isLocked,
  ...props
}) => {
  const classes = useStyles();
  const notify = useNotify();
  const history = useHistory();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);

  const isZapierSynced = useMemo(() => {
    return !!record?.externalStatus || hasExternalId(record || {});
  }, [record]);
  const isSyncing =
    isSyncEnabled && record.externalStatus !== ExternalStatus.SYNCED;

  const shouldOfferVoidEmail = useMemo(() => {
    return !!record?.sentDate && !isZapierSynced;
  }, [record?.sentDate, isZapierSynced]);

  const handleConfirmOpen = () => setConfirmOpen(true);
  const handleConfirmClose = () => setConfirmOpen(false);

  const handleVoidClick = () => {
    handleConfirmOpen();
  };

  const voidInvoice = async ({ emailBody, toEmail } = {}) => {
    setIsDeleting(true);
    setConfirmOpen(false);
    setEmailOpen(false);

    try {
      const voidedInvoice = {
        ...record,
        active: "false",
        status: InvoiceStatus.VOID,
      };

      // always void the invoice, do not delete it
      await performUpdateInvoiceMini(voidedInvoice);

      if (emailBody) {
        // Fire-and-forget (consistent with Send Invoice behavior)
        sendEmail(
          emailBody,
          "invoices:DeepDeleteButton:voidInvoice:sendVoidedEmail",
          notify,
        );

        const emailHistoryNote = {
          text: `Voided invoice email sent to: ${toEmail || ""}`.trim(),
          parentId: record.id,
          parentType: ResourceType.INVOICE,
        };
        logHistory(emailHistoryNote);
      }

      const shipmentHistoryNote = {
        text: `Invoice ${record.invoiceNumber} deleted.`,
        by: window.appUser?.fullName,
        date: new Date(),
        type: "GENERIC",
      };
      const linkedInboundById = new Map();
      const linkedOutboundById = new Map();

      // Fallback: query current invoiceId links first to avoid ES reindex lag
      let linkedInbounds = [];
      let linkedOutbounds = [];
      try {
        const reloadYardId = window.appUser?.reloadYardId;
        [linkedInbounds, linkedOutbounds] = await Promise.all([
          fetchShipmentsByInvoiceId({
            query: inboundShipmentByReloadYardByInvoice,
            queryName: "inboundShipmentByReloadYardByInvoice",
            invoiceId: record.id,
            reloadYardId,
          }),
          fetchShipmentsByInvoiceId({
            query: outboundShipmentByReloadYardByInvoice,
            queryName: "outboundShipmentByReloadYardByInvoice",
            invoiceId: record.id,
            reloadYardId,
          }),
        ]);
      } catch (error) {
        logError("DeepDeleteButton:voidInvoice:fallbackSearch", error, false);
      }

      if (record.lineItems?.items?.length) {
        for (const item of record.lineItems.items) {
          if (item.inboundShipmentId) {
            linkedInboundById.set(
              item.inboundShipmentId,
              item.inboundShipment || null,
            );
          } else if (item.outboundShipmentId) {
            linkedOutboundById.set(
              item.outboundShipmentId,
              item.outboundShipment || null,
            );
          }
        }
      }

      linkedInbounds.forEach((inbound) => {
        if (!linkedInboundById.has(inbound.id)) {
          linkedInboundById.set(inbound.id, inbound);
        }
      });
      linkedOutbounds.forEach((outbound) => {
        if (!linkedOutboundById.has(outbound.id)) {
          linkedOutboundById.set(outbound.id, outbound);
        }
      });

      for (const [id, inbound] of linkedInboundById.entries()) {
        try {
          const toUpdate = {
            id,
            invoiceId: " ", // required for secondary index
            history: [...(inbound?.history || []), shipmentHistoryNote],
          };
          await performUpdateInboundMini(toUpdate);
        } catch (err) {
          logError("DeepDeleteButton: Failed to update shipment", err, false);
        }
      }

      for (const [id, outbound] of linkedOutboundById.entries()) {
        try {
          const toUpdate = {
            id,
            invoiceId: " ", // required for secondary index
            history: [...(outbound?.history || []), shipmentHistoryNote],
          };
          await performUpdateOutboundMini(toUpdate);
        } catch (err) {
          logError("DeepDeleteButton: Failed to update shipment", err, false);
        }
      }

      if (record.lineItems?.items) {
        const results = await Promise.allSettled(
          record.lineItems.items.map((item) => {
            const invoiceServiceInput = {
              ...item,
              inboundShipmentId: null,
              outboundShipmentId: null,
            };
            return performUpdateInvoiceServiceMini(invoiceServiceInput);
          }),
        );

        const resError = results.find((itm) => itm.status === "rejected");
        if (resError)
          logError(
            "invoices:DeepDeleteButton:handleDelete:updateInvoiceService",
            resError.reason,
            false,
          );
      }

      const historyNote = {
        parentId: record.id,
        parentType: ResourceType.INVOICE,
        text: `Invoice voided`,
      };
      logHistory(historyNote);

      if (isSyncEnabled) {
        triggerEvent({ externalId: record.externalId }, "invoiceVoided").catch(
          (error) => {
            logError("invoices:DeepDeleteButton:triggerEvent", error);
            setInvoiceSyncFailed(record);
          },
        );
      }

      notify("Invoice voided successfully", "success");
      const trimmedBackPath = trimBackPath(backPath, record);
      deepRedirect(history, trimmedBackPath);
    } catch (err) {
      setInvoiceSyncFailed(record);
      logError("invoices:DeepDeleteButton:voidInvoice", err, notify);
    } finally {
      setIsDeleting(false);
      setConfirmOpen(false);
      setEmailOpen(false);
    }
  };

  const handleConfirmVoid = () => {
    if (shouldOfferVoidEmail) {
      setConfirmOpen(false);
      setEmailOpen(true);
      return;
    }
    voidInvoice();
  };

  const handleEmailCancelVoid = () => voidInvoice();

  const handleEmailSendVoid = async ({ to, emailBody }) => {
    await voidInvoice({ emailBody, toEmail: to });
  };

  return isSyncing ? (
    <Tooltip title="Invoice is syncing">
      <span>
        {/* <span> is required to wrap a disabled button so tooltip still works */}
        <Button
          label="Void"
          disabled
          className={classnames(
            "ra-delete-button",
            classes.deleteButton,
            className,
          )}
          {...props}
        >
          <DeleteSyncIcon />
        </Button>
      </span>
    </Tooltip>
  ) : (
    <>
      <Button
        {...props}
        label="Void"
        disabled={isDeleting || isSaving || isSyncing}
        onClick={handleVoidClick}
        className={classnames(
          "ra-delete-button",
          classes.deleteButton,
          className,
        )}
      >
        {isSyncEnabled ? <DeleteSyncIcon /> : <DeleteIcon />}
      </Button>
      <Confirm
        isOpen={confirmOpen}
        loading={isDeleting}
        title="Delete Confirmation"
        content="Are you sure you want to void this Invoice?"
        onConfirm={handleConfirmVoid}
        onClose={handleConfirmClose}
      />
      {emailOpen && (
        <EmailInvoiceDialog
          invoice={{ ...record, active: "false", status: InvoiceStatus.VOID }}
          emailOpen={emailOpen}
          setEmailOpen={setEmailOpen}
          sending={isDeleting}
          setSending={setIsDeleting}
          notify={notify}
          dialogTitle="Void Invoice"
          message="This invoice was previously sent. Send a void notification email to the customer?"
          subjectPrefix="VOIDED "
          onCancel={handleEmailCancelVoid}
          onSubmit={handleEmailSendVoid}
        />
      )}
    </>
  );
};
